import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../../../../assets/logo.png';
import { useAuth } from '../../../../contexts/AuthContext';

const SignUp = () => {
  const [form, setForm] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    password: '',
    password2: '',
    referral_code: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors]   = useState({});
  const navigate = useNavigate();
  const { register } = useAuth();

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (form.password !== form.password2) {
      setErrors({ password2: 'Passwords do not match.' });
      return;
    }

    setLoading(true);

    const payload = {
      username:     form.username.trim(),
      email:        form.email.trim(),
      first_name:   form.first_name.trim(),
      last_name:    form.last_name.trim(),
      phone_number: form.phone_number.trim(),
      password:     form.password,
      password2:    form.password2,
      user_type:    'user', // client only on this app
    };
    if (form.referral_code.trim()) payload.referral_code = form.referral_code.trim();

    const result = await register(payload);

    if (result.success) {
      // Backend returns phone_number — pass it to OTP page
      navigate('/verify-phone', { state: { phone_number: result.phone_number } });
    } else {
      // If backend returned field-level errors they'll be in result.message
      setErrors({ general: result.message });
    }

    setLoading(false);
  };

  return (
    <div className="signup-container">
      <div className="header-container">
        <div className="logo-container">
          <img src={Logo} alt="Fagi Errands" className="logo-img" />
          <span className="brand-name">Fagi Errands</span>
        </div>
        <div className="nav-links">
          <Link to="/login" className="nav-link">Login</Link>
          <Link to="/contact" className="contact-button">Contact Us</Link>
        </div>
      </div>

      <div className="form-wrapper">
        <h2>Create Account</h2>

        {errors.general && <div className="error-box">{errors.general}</div>}

        <form onSubmit={handleSubmit} autoComplete="off">
          <Field label="Username" error={errors.username}>
            <input value={form.username} onChange={e => update('username', e.target.value)}
              placeholder="Username" required className="input-field" />
          </Field>

          <Field label="Email" error={errors.email}>
            <input type="email" value={form.email} onChange={e => update('email', e.target.value)}
              placeholder="Email Address" required className="input-field" />
          </Field>

          <div className="two-col">
            <Field label="First Name" error={errors.first_name}>
              <input value={form.first_name} onChange={e => update('first_name', e.target.value)}
                placeholder="First Name" required className="input-field" />
            </Field>
            <Field label="Last Name" error={errors.last_name}>
              <input value={form.last_name} onChange={e => update('last_name', e.target.value)}
                placeholder="Last Name" required className="input-field" />
            </Field>
          </div>

          <Field label="Phone Number" error={errors.phone_number}>
            <input type="tel" value={form.phone_number} onChange={e => update('phone_number', e.target.value)}
              placeholder="e.g. 0712345678 or 254712345678" required className="input-field" />
          </Field>

          <Field label="Password" error={errors.password}>
            <input type="password" value={form.password} onChange={e => update('password', e.target.value)}
              placeholder="Min 8 chars, include number & special char" required className="input-field" />
          </Field>

          <Field label="Confirm Password" error={errors.password2}>
            <input type="password" value={form.password2} onChange={e => update('password2', e.target.value)}
              placeholder="Confirm Password" required className="input-field" />
          </Field>

          <Field label="Referral Code (optional)">
            <input value={form.referral_code} onChange={e => update('referral_code', e.target.value)}
              placeholder="Referral Code" className="input-field" />
          </Field>

          <p className="hint">
            Already have an account? <Link to="/login" className="link">Login here</Link>
          </p>

          <button type="submit" disabled={loading} className="primary-btn">
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
      </div>

      <style>{`
        .signup-container {
          min-height: 100vh;
          background: radial-gradient(at center, #49AFAF, #A8DFF4);
          font-family: Arial, sans-serif;
        }
        .header-container {
          display: flex; justify-content: space-between;
          align-items: center; padding: 20px 40px;
        }
        .logo-container { display: flex; align-items: center; gap: 10px; }
        .logo-img { width: 40px; height: 40px; object-fit: contain; }
        .brand-name { font-size: 20px; font-weight: bold; color: #111827; }
        .nav-links { display: flex; align-items: center; gap: 20px; }
        .nav-link { color: #111827; text-decoration: none; font-weight: 500; }
        .contact-button {
          background: #3b82f6; color: white; padding: 8px 20px;
          border-radius: 999px; text-decoration: none; font-weight: 500;
        }
        .form-wrapper { max-width: 480px; margin: 20px auto 40px; padding: 0 20px; }
        h2 { font-size: 26px; font-weight: bold; color: #111827; margin-bottom: 20px; text-align:center; }
        .error-box {
          background: #fee2e2; border: 1px solid #f87171; color: #b91c1c;
          padding: 12px; border-radius: 6px; margin-bottom: 14px; font-size: 14px;
        }
        .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .input-field {
          width: 100%; padding: 12px 14px; border: 1px solid #d1d5db;
          border-radius: 6px; font-size: 15px; background: white;
          box-sizing: border-box; margin-bottom: 4px;
        }
        .input-field:focus { outline: none; border-color: #3b82f6; }
        .hint { font-size: 14px; color: #374151; margin: 12px 0; text-align:center; }
        .link { color: #3b82f6; text-decoration: none; }
        .link:hover { text-decoration: underline; }
        .primary-btn {
          width: 100%; padding: 13px; background: #3b82f6; color: white;
          border: none; border-radius: 50px; font-size: 16px; font-weight: 500;
          cursor: pointer; margin-top: 8px; transition: background 0.2s;
        }
        .primary-btn:hover { background: #2563eb; }
        .primary-btn:disabled { background: #9ca3af; cursor: not-allowed; }
        @media(max-width:480px) { .two-col { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
};

// Simple field wrapper
const Field = ({ label, error, children }) => (
  <div style={{ marginBottom: 10 }}>
    {children}
    {error && <span style={{ color:'#dc2626', fontSize:12, display:'block', marginTop:2 }}>{error}</span>}
  </div>
);

export default SignUp;
